import { z } from "zod";
import { PRESET_TOGGLE_ICON_IDS } from "./presetIcons";

export const elementDefSchema = z.object({
  id: z.string().max(200),
  selector: z.string().max(500),
  label: z.string().max(200),
  purpose: z.string().max(500).optional(),
});

/** Document per pagină (fapte de interviu sau, istoric, dintr-un fișier încărcat) — doar faptele distilate persistă. */
export const pageDocSchema = z.object({
  id: z.string().max(100), // slug stabil derivat din numele fișierului
  name: z.string().max(200), // numele fișierului original
  addedAt: z.string().max(30), // ISO date
  facts: z.array(z.string().max(500)).max(30), // faptele distilate de LLM
});

export const pageSchema = z.object({
  id: z.string().max(200),
  urlPattern: z.string().max(500),
  title: z.string().max(200),
  /** rezumat business: la ce folosește pagina, ce realizează utilizatorii aici */
  summary: z.string().max(2000).optional(),
  /**
   * Ce ÎNTREBĂRI își găsesc răspunsul pe pagina asta („return window and refund
   * timeline"), nu ce afișează ea. E harta după care agentul rutează: din site
   * index alege unde trimite omul, apoi snapshot-ul paginii noi îi dă restul.
   *
   * De când runtime-ul primește un snapshot viu, ce e PE pagină se vede direct;
   * ce nu se poate ști din afară e la ce e bună pagina. Configurile de dinainte
   * n-au câmpul — default([]), deci parsează în continuare.
   */
  topics: z.array(z.string().max(60)).max(10).default([]),
  /**
   * Nu se mai extrag la scanare (snapshot-ul + `idx` le înlocuiesc la runtime),
   * dar câmpul rămâne: configurile vechi publicate le au, `resolve.ts` le
   * folosește ca treaptă de rezervă, `fallbackAgent.ts` țintește prin ele, iar
   * picker-ul manual din panou încă poate adăuga unul. Configurile noi vor avea
   * lista goală.
   */
  elements: z.array(elementDefSchema).max(200).default([]),
  /** întrebări la care doar clientul poate răspunde (interviul le va pune) */
  openQuestions: z.array(z.string().max(300)).max(5).default([]),
  /** fapte aflate la interviu (răspunsurile clientului), separate de rezumatul din DOM */
  notes: z.array(z.string().max(500)).max(20).default([]),
  /** starea paginii în configurator: citită → în interviu → confirmată; exclusă = agentul o sare */
  builderState: z.enum(["read", "interviewing", "confirmed", "excluded"]).optional(),
  // fără limită de documente: costul e deja plafonat prin quota LLM (checkQuota/recordUsage)
  docs: z.array(pageDocSchema).default([]),
});

/** Profilul de business global — colectat la kickoff-ul configuratorului. */
export const businessProfileSchema = z.object({
  description: z.string().max(2000).optional(),
  audience: z.string().max(1000).optional(),
  topTasks: z.array(z.string().max(300)).max(10).default([]),
  language: z.string().max(20).optional(),
});

/** Elemente prezente pe toate paginile (meniu, header, footer) — o singură dată, la nivel global. */
export const layoutSchema = z.object({
  elements: z.array(elementDefSchema).max(100).default([]),
  // documente globale (nu per pagină): aceleași reguli ca docs de pagină, fără limită de câte
  docs: z.array(pageDocSchema).default([]),
});

/**
 * Plafonul unic pentru `apps.agent_notes` (brieful liber al agentului). O singură
 * sursă de adevăr — înainte erau 4 copii cu valori diferite (2000 în dashboard,
 * 4000 în admin.ts, brieful din builder salva ~3900 fără să bată nicăieri),
 * observat pe staging 21 aug 2026 ca un contor „3879/2000" roșu deși notele
 * chiar erau salvate. Decizia lui Radu: 4000 peste tot.
 */
export const AGENT_NOTES_MAX_CHARS = 4000;

/**
 * Brief-ul asistentului, generat prin distill din draftul scanat + intervievat
 * (docs/plan-brief-distill.md). Trăiește în DRAFT (nu în configul publicat):
 * canalul live rămâne `apps.agent_notes`, unde textul ajunge doar la „Aplică"
 * (human-in-the-loop). Golurile sunt întrebările pe care doar proprietarul le
 * poate închide — interviul le pune primele.
 */
export const briefSchema = z.object({
  text: z.string().max(AGENT_NOTES_MAX_CHARS), // generat, apoi editat de proprietar în panou; devine agent notes la „Aplică"
  gaps: z.array(z.object({ question: z.string().max(500), why: z.string().max(500) })).max(8),
  generatedAt: z.string().max(30), // ISO date
  model: z.string().max(100), // cu ce model s-a generat (audit + regres)
  appliedAt: z.string().max(30).optional(), // setat când textul a fost scris în agent_notes
});

export const appConfigSchema = z.object({
  appId: z.string().max(100),
  version: z.string().max(50),
  status: z.enum(["draft", "published", "archived"]),
  business: businessProfileSchema.optional(),
  layout: layoutSchema.default({ elements: [], docs: [] }),
  pages: z.array(pageSchema).max(500),
  brief: briefSchema.optional(),
});

/**
 * Vocile dintre care alege clientul în dashboard (branding → voice). Ordinea e
 * și ordinea din select; etichetele UI stau în dashboard (i18n), aici doar
 * valorile stocate.
 */
export const WIDGET_VOICES = ["standard", "serious", "storyteller"] as const;
export type WidgetVoice = (typeof WIDGET_VOICES)[number];

/**
 * Personalizarea widgetului (Growth+) — setare de SITE, nu versiune de config
 * publicată: schimbarea ei nu cere republish. Vezi docs/plan-features.md §1.
 */
export const widgetBrandingSchema = z.object({
  assistantName: z.string().max(60).optional(),
  /** avatar/logo — fie un URL extern ales de client, fie unul de-al nostru
   *  (`/v1/apps/:appId/logo`, vezi routes/assets.ts + services/appLogo.ts)
   *  după un upload din dashboard. Doar http(s): `.url()` acceptă și
   *  `data:`/`javascript:`; deși e setat de owner (nu de vizitator) și
   *  randat ca `<img src>` (nu execută js), blocăm schemele non-http ca să nu
   *  apară trucuri cu `data:` sau mixed-content. (S-16) */
  logoUrl: z
    .string()
    .url()
    .max(500)
    .refine((u) => /^https?:$/.test(new URL(u).protocol), "logoUrl trebuie să fie http(s)")
    .optional(),
  /** Iconiță predefinită în buton (vezi presetIcons.ts) — alternativa „safe" la un
   *  logo încărcat. Poate coexista cu `logoUrl` în DB; SDK-ul dă mereu prioritate
   *  logo-ului (widget.ts mount), iar dashboardul le face exclusive doar în UI. */
  presetIcon: z.enum(PRESET_TOGGLE_ICON_IDS).optional(),
  primaryColor: z.string().regex(/^#[0-9a-f]{6}$/i).optional(),
  /** Cerneala așezată PESTE `primaryColor` (text de header, bula vizitatorului,
   *  butonul ✕). Gol → se calculează automat pentru contrast AA (inkOnPrimary);
   *  setată → o respectăm ca atare, alegerea proprietarului. Vezi widget.ts mount(). */
  textColor: z.string().regex(/^#[0-9a-f]{6}$/i).optional(),
  /** „Just logo": bulina închisă (`.aig-toggle`) renunță la cerc/fundal/umbră și
   *  arată DOAR imaginea (`.aig-toggle--logo-only`). Fără `logoUrl` n-are efect —
   *  SDK-ul o ignoră, altfel ar rămâne marca noastră plutind fără nimic dedesubt. */
  logoOnly: z.boolean().optional(),
  /** CSS custom injectat în shadow root (doar white-label — gate în admin.ts +
   *  widgetBrandingFor). `launcherCss` țintește butonul (`.aig-toggle`), `chatCss`
   *  panoul (`.aig-panel` și copiii). Se injectează prin `style.textContent`, deci
   *  nu poate „sparge" tag-ul și CSS-ul nu execută JS; blast-radius = shadow root-ul
   *  widgetului. Plafon de lungime ca să nu umfle payload-ul din /config. */
  launcherCss: z.string().max(4000).optional(),
  chatCss: z.string().max(4000).optional(),
  welcomeMessage: z.string().max(300).optional(),
  /** Mesajul servit când asistentul chiar nu are un răspuns (fallback-ul keyword
   *  din agent.ts — LLM picat de tot sau cotă blocată). Gol → textul default din
   *  fallbackAgent.ts. Ajunge la vizitator prin /ask ca orice mesaj, deci e
   *  setat de owner și nesensibil — poate sta liniștit în branding. */
  fallbackMessage: z.string().max(300).optional(),
  tone: z.enum(["friendly", "professional", "playful", "concise"]).optional(),
  /** Suprafața „orb": orbul care se morfează în card HUD, în locul panoului
   *  clasic (sdk/orbchat/). Opt-in per app, absent/false = panoul clasic —
   *  clienții existenți nu văd nicio schimbare fără să bifeze ei. `?aig_orb_chat=1`
   *  rămâne override de depanare pe tab (sdk.ts). */
  orbChat: z.boolean().optional(),
  /** Vocea agentului în modul de voce al orb-chatului. Nume semantice, nu chei
   *  de provider: maparea spre vocile reale (ElevenLabs) stă în SDK
   *  (voice/voiceName.ts), deci un schimb de provider nu atinge setarea
   *  clientului. Absent = "standard" (vocea configurată pe agent).
   *  `?voice=1|2|3` din URL bate setarea, pe durata tabului. */
  voice: z.enum(WIDGET_VOICES).optional(),
});

/**
 * Platformele pentru care avem instrucțiuni de instalare predefinite în dashboard.
 * `generic` = orice sit HTML (fallback-ul universal); `gtm` nu e o platformă, ci o
 * cale de instalare (Google Tag Manager) — apare ca opțiune de selectat, dar
 * detectarea o raportează separat (un sit WordPress POATE avea GTM). Sursa
 * detectării: services/platformDetect.ts (backend); selectorul: cardul Install.
 */
export const INSTALL_PLATFORMS = ["wordpress", "shopify", "wix", "squarespace", "webflow", "gtm", "generic"] as const;
export const installPlatformSchema = z.enum(INSTALL_PLATFORMS);

export type ElementDef = z.infer<typeof elementDefSchema>;
export type Layout = z.infer<typeof layoutSchema>;
export type BusinessProfile = z.infer<typeof businessProfileSchema>;
export type PageDoc = z.infer<typeof pageDocSchema>;
export type PageDef = z.infer<typeof pageSchema>;
export type Brief = z.infer<typeof briefSchema>;
export type AppConfig = z.infer<typeof appConfigSchema>;
export type WidgetBranding = z.infer<typeof widgetBrandingSchema>;
export type InstallPlatform = z.infer<typeof installPlatformSchema>;
